import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { toast } from "sonner";
import { API, formatApiErrorDetail, describeApiError } from "@/lib/api";
import { clearClientPortalToken, createClientPortalApi, getClientPortalToken, setClientPortalToken } from "@/lib/clientPortalApi";
import { ArrowSquareOut, CircleNotch, DownloadSimple, FileText, Receipt, SignOut, ChatCircleText, PaperPlaneTilt } from "@phosphor-icons/react";

export default function ClientPortal() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [branding, setBranding] = useState(null);
  const [client, setClient] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingUp, setSettingUp] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [paying, setPaying] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [setupForm, setSetupForm] = useState({ name: "", password: "" });
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [messageForm, setMessageForm] = useState({ message_type: "general", subject: "", body: "", invoice_id: "" });
  const portalApi = useMemo(() => createClientPortalApi(slug), [slug]);
  const inviteToken = searchParams.get("invite") || "";

  const loadBranding = async () => {
    const data = await fetch(`${API}/client-portal/business/${slug}`).then((r) => r.json());
    setBranding(data);
  };

  const loadDashboard = async () => {
    const [{ data: me }, { data: dash }] = await Promise.all([portalApi.get("/client-portal/me"), portalApi.get("/client-portal/dashboard")]);
    setClient(me.client);
    setDashboard(dash);
  };

  useEffect(() => {
    (async () => {
      try {
        await loadBranding();
        if (getClientPortalToken(slug)) await loadDashboard();
      } catch {}
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, [slug]);

  const setupPortal = async () => {
    setSettingUp(true);
    try {
      const { data } = await fetch(`${API}/client-portal/setup`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token: inviteToken, ...setupForm }) }).then(async (r) => {
        const body = await r.json();
        if (!r.ok) throw new Error(formatApiErrorDetail(body.detail));
        return { data: body };
      });
      setClientPortalToken(slug, data.token);
      setClient(data.client);
      await loadDashboard();
      toast.success("Client portal ready");
    } catch (err) {
      toast.error(err.message || "Couldn't finish portal setup.");
    } finally {
      setSettingUp(false);
    }
  };

  const login = async () => {
    setLoggingIn(true);
    try {
      const { data } = await fetch(`${API}/client-portal/login/${slug}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(loginForm) }).then(async (r) => {
        const body = await r.json();
        if (!r.ok) throw new Error(formatApiErrorDetail(body.detail));
        return { data: body };
      });
      setClientPortalToken(slug, data.token);
      setClient(data.client);
      await loadDashboard();
    } catch (err) {
      toast.error(err.message || "Couldn't log in.");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    clearClientPortalToken(slug);
    setClient(null);
    setDashboard(null);
  };

  const payInvoice = async (invoiceId) => {
    setPaying(invoiceId);
    try {
      const { data } = await portalApi.post(`/client-portal/invoices/${invoiceId}/pay`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setPaying("");
    }
  };

  const viewInvoice = async (invoice) => {
    try { await portalApi.post(`/client-portal/invoices/${invoice.id}/view`); } catch {}
    window.open(`/i/${invoice.public_id}`, "_blank", "noopener,noreferrer");
  };

  const downloadDocument = async (docId, filename) => {
    try {
      const res = await portalApi.get(`/client-portal/documents/${docId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename || "document"; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  };

  const sendMessage = async () => {
    if (!messageForm.body.trim()) return toast.error("Write a message first.");
    setSendingMessage(true);
    try {
      await portalApi.post("/client-portal/messages", {
        message_type: messageForm.message_type,
        subject: messageForm.subject,
        body: messageForm.body,
        invoice_id: messageForm.invoice_id || null,
      });
      setMessageForm((f) => ({ ...f, subject: "", body: "", invoice_id: "" }));
      await loadDashboard();
      toast.success("Message sent");
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: "#F8F7F4" }}>Loading…</div>;

  const theme = branding?.theme || dashboard?.business?.theme || { accent: "#1A2744", surface: "#F8F7F4", hero: "linear-gradient(135deg,#1A2744,#4A6FA5)" };

  return (
    <div className="min-h-screen" style={{ background: theme.surface }} data-testid="client-portal-page">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="rounded-[28px] p-8 text-white" style={{ background: theme.hero }}>
          {branding?.logo_url && <img src={branding.logo_url} alt="Business logo" className="h-14 w-auto object-contain mb-5" data-testid="client-portal-logo" />}
          <p className="text-xs uppercase tracking-[0.18em] font-semibold text-white/75">Private client portal</p>
          <h1 className="font-display text-4xl font-bold mt-2" data-testid="client-portal-title">Welcome to {branding?.business_name || dashboard?.business?.name}'s Client Portal</h1>
          <p className="text-sm mt-3 text-white/80">{branding?.welcome_message || dashboard?.business?.welcome_message}</p>
        </div>

        {!client ? (
          <div className="max-w-xl mx-auto mt-8 rounded-[28px] bg-white border p-8" style={{ borderColor: "rgba(229,231,235,0.85)" }}>
            <h2 className="font-display text-2xl font-bold" style={{ color: "#1A2744" }}>{inviteToken ? "Set your portal password" : "Client login"}</h2>
            <div className="space-y-4 mt-6">
              {inviteToken ? (
                <>
                  <input value={setupForm.name} onChange={(e) => setSetupForm((f) => ({ ...f, name: e.target.value }))} placeholder="Your name" data-testid="client-portal-setup-name" className="w-full rounded-xl border px-4 py-3 text-sm" style={{ borderColor: "#D8DADF" }} />
                  <input type="password" value={setupForm.password} onChange={(e) => setSetupForm((f) => ({ ...f, password: e.target.value }))} placeholder="Create password" data-testid="client-portal-setup-password" className="w-full rounded-xl border px-4 py-3 text-sm" style={{ borderColor: "#D8DADF" }} />
                  <button onClick={setupPortal} disabled={settingUp} data-testid="client-portal-setup-submit" className="w-full rounded-full px-5 py-3 text-sm font-semibold text-white" style={{ background: theme.accent }}>{settingUp ? "Setting up…" : "Create portal access"}</button>
                </>
              ) : (
                <>
                  <input value={loginForm.email} onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" data-testid="client-portal-login-email" className="w-full rounded-xl border px-4 py-3 text-sm" style={{ borderColor: "#D8DADF" }} />
                  <input type="password" value={loginForm.password} onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))} placeholder="Password" data-testid="client-portal-login-password" className="w-full rounded-xl border px-4 py-3 text-sm" style={{ borderColor: "#D8DADF" }} />
                  <button onClick={login} disabled={loggingIn} data-testid="client-portal-login-submit" className="w-full rounded-full px-5 py-3 text-sm font-semibold text-white" style={{ background: theme.accent }}>{loggingIn ? "Signing in…" : "Log in"}</button>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-6 mt-8">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm" style={{ color: "#4B5563" }}>Signed in as {client.name || client.email}</p>
                <p className="text-xs mt-1" style={{ color: "#6B7280" }}>Everything here is private to your account.</p>
              </div>
              <button onClick={logout} data-testid="client-portal-logout" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold" style={{ borderColor: "#D8DADF", color: "#1A2744" }}><SignOut size={16} weight="bold" /> Log out</button>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-[1.15fr_0.85fr] gap-6">
              <div className="space-y-6">
                {dashboard?.visibility?.invoices !== false && (
                  <div className="rounded-[24px] bg-white border p-6" style={{ borderColor: "rgba(229,231,235,0.85)" }} data-testid="client-portal-outstanding-card">
                    <div className="flex items-center gap-2 mb-4"><Receipt size={18} weight="duotone" color={theme.accent} /><h3 className="font-display text-xl font-bold" style={{ color: "#1A2744" }}>Outstanding invoices</h3></div>
                    <div className="space-y-4">
                      {(dashboard?.outstanding_invoices || []).length === 0 ? <p className="text-sm" style={{ color: "#6B7280" }}>No open balances right now.</p> : dashboard.outstanding_invoices.map((inv) => (
                        <div key={inv.id} className="rounded-2xl p-4" style={{ background: "#F8F7F4" }} data-testid={`client-portal-invoice-${inv.id}`}>
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                              <p className="font-semibold" style={{ color: "#1A2744" }}>Invoice #{inv.number}</p>
                              <p className="text-xs mt-1" style={{ color: "#6B7280" }}>Due {inv.due_date || "—"}</p>
                            </div>
                            <p className="font-display text-xl font-bold" style={{ color: theme.accent }}>${Number(inv.total || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                          </div>
                          <div className="flex flex-wrap gap-3 mt-4">
                            <button onClick={() => viewInvoice(inv)} data-testid={`client-portal-view-invoice-${inv.id}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold" style={{ borderColor: "#D8DADF", color: "#1A2744" }}><FileText size={15} weight="bold" /> View invoice</button>
                            <button onClick={() => payInvoice(inv.id)} disabled={paying === inv.id} data-testid={`client-portal-pay-invoice-${inv.id}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white" style={{ background: theme.accent }}>{paying === inv.id ? <><CircleNotch size={15} className="animate-spin" /> Opening…</> : <>Pay Now <ArrowSquareOut size={14} weight="bold" /></>}</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {dashboard?.visibility?.payments !== false && (
                  <div className="rounded-[24px] bg-white border p-6" style={{ borderColor: "rgba(229,231,235,0.85)" }} data-testid="client-portal-payment-history-card">
                    <h3 className="font-display text-xl font-bold mb-4" style={{ color: "#1A2744" }}>Payment history</h3>
                    <div className="space-y-3">
                      {(dashboard?.payment_history || []).length === 0 ? <p className="text-sm" style={{ color: "#6B7280" }}>No paid invoices yet.</p> : dashboard.payment_history.map((inv) => <div key={inv.id} className="flex items-center justify-between rounded-2xl p-4" style={{ background: "#F8F7F4" }}><div><p className="font-semibold" style={{ color: "#1A2744" }}>Invoice #{inv.number}</p><p className="text-xs mt-1" style={{ color: "#6B7280" }}>{inv.issue_date || inv.created_at?.slice(0,10)}</p></div><span className="font-semibold" style={{ color: "#2F6B4F" }}>${Number(inv.total || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>)}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                {dashboard?.visibility?.appointments !== false && (dashboard?.appointments || []).length > 0 && (
                  <div className="rounded-[24px] bg-white border p-6" style={{ borderColor: "rgba(229,231,235,0.85)" }} data-testid="client-portal-appointments-card">
                    <h3 className="font-display text-xl font-bold mb-4" style={{ color: "#1A2744" }}>Appointment history</h3>
                    <div className="space-y-3">{dashboard.appointments.map((appt) => <div key={appt.id} className="rounded-2xl p-4" style={{ background: "#F8F7F4" }}><p className="font-semibold" style={{ color: "#1A2744" }}>{appt.item_name}</p><p className="text-xs mt-1" style={{ color: "#6B7280" }}>{appt.date} · {appt.start_time}–{appt.end_time}</p></div>)}</div>
                  </div>
                )}
                {dashboard?.visibility?.documents !== false && (
                  <div className="rounded-[24px] bg-white border p-6" style={{ borderColor: "rgba(229,231,235,0.85)" }} data-testid="client-portal-documents-card">
                    <h3 className="font-display text-xl font-bold mb-4" style={{ color: "#1A2744" }}>Shared documents</h3>
                    <div className="space-y-3">{(dashboard?.documents || []).length === 0 ? <p className="text-sm" style={{ color: "#6B7280" }}>Nothing shared yet.</p> : dashboard.documents.map((doc) => <button key={doc.id} onClick={() => downloadDocument(doc.id, doc.filename)} data-testid={`client-portal-download-${doc.id}`} className="w-full flex items-center justify-between rounded-2xl p-4 text-left" style={{ background: "#F8F7F4" }}><span style={{ color: "#1A2744" }}>{doc.label || doc.filename}</span><DownloadSimple size={16} weight="bold" color={theme.accent} /></button>)}</div>
                  </div>
                )}
                <div className="rounded-[24px] bg-white border p-6" style={{ borderColor: "rgba(229,231,235,0.85)" }} data-testid="client-portal-messages-card">
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-2"><ChatCircleText size={18} weight="duotone" color={theme.accent} /><h3 className="font-display text-xl font-bold" style={{ color: "#1A2744" }}>Messages</h3></div>
                    {(dashboard?.message_unread_count || 0) > 0 && <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ background: "#FFF6DE", color: theme.accent }} data-testid="client-portal-message-unread">{dashboard.message_unread_count} unread</span>}
                  </div>
                  <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                    {(dashboard?.messages || []).length === 0 ? <p className="text-sm" style={{ color: "#6B7280" }}>No messages yet. Use the form below to contact the business owner securely.</p> : dashboard.messages.map((msg) => <div key={msg.id} className="rounded-2xl p-4" style={{ background: msg.sender_type === "client" ? "#FFF9EF" : "#F8F7F4" }} data-testid={`client-portal-message-${msg.id}`}><div className="flex items-center justify-between gap-3"><p className="text-xs font-semibold" style={{ color: "#1A2744" }}>{msg.sender_type === "client" ? "You" : (dashboard?.business?.contact_name || dashboard?.business?.name)} · {msg.message_type.replaceAll("_", " ")}</p><span className="text-[11px]" style={{ color: "#6B7280" }}>{new Date(msg.created_at).toLocaleString()}</span></div><p className="text-sm font-semibold mt-2" style={{ color: "#1A2744" }}>{msg.subject}</p>{msg.invoice_number && <p className="text-xs mt-1" style={{ color: theme.accent }}>Invoice #{msg.invoice_number}</p>}<p className="text-sm mt-2 whitespace-pre-line" style={{ color: "#4B5563" }}>{msg.body}</p></div>)}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                    <select value={messageForm.message_type} onChange={(e) => setMessageForm((f) => ({ ...f, message_type: e.target.value }))} data-testid="client-portal-message-type" className="rounded-xl border px-3 py-2 text-sm bg-white" style={{ borderColor: "#D8DADF" }}>
                      <option value="general">General message</option>
                      <option value="invoice_question">Invoice question</option>
                      <option value="document_request">Document request</option>
                      <option value="payment_confirmation">Payment confirmation</option>
                    </select>
                    <select value={messageForm.invoice_id} onChange={(e) => setMessageForm((f) => ({ ...f, invoice_id: e.target.value }))} data-testid="client-portal-message-invoice" className="rounded-xl border px-3 py-2 text-sm bg-white" style={{ borderColor: "#D8DADF" }}>
                      <option value="">No linked invoice</option>
                      {(dashboard?.message_invoice_options || []).map((inv) => <option key={inv.id} value={inv.id}>Invoice #{inv.number}</option>)}
                    </select>
                    <input value={messageForm.subject} onChange={(e) => setMessageForm((f) => ({ ...f, subject: e.target.value }))} data-testid="client-portal-message-subject" placeholder="Subject" className="rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "#D8DADF" }} />
                  </div>
                  <textarea value={messageForm.body} onChange={(e) => setMessageForm((f) => ({ ...f, body: e.target.value }))} rows={4} data-testid="client-portal-message-body" placeholder="Ask a question, request a document, or confirm a payment…" className="w-full mt-3 rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "#D8DADF" }} />
                  <div className="flex justify-end mt-3">
                    <button onClick={sendMessage} disabled={sendingMessage} data-testid="client-portal-message-send" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white disabled:opacity-60" style={{ background: theme.accent }}>
                      {sendingMessage ? <><CircleNotch size={15} className="animate-spin" /> Sending…</> : <><PaperPlaneTilt size={15} weight="fill" /> Send message</>}
                    </button>
                  </div>
                </div>
                <div className="rounded-[24px] bg-white border p-6" style={{ borderColor: "rgba(229,231,235,0.85)" }} data-testid="client-portal-contact-card">
                  <h3 className="font-display text-xl font-bold mb-4" style={{ color: "#1A2744" }}>Contact</h3>
                  <p className="text-sm" style={{ color: "#1A2744" }}>{dashboard?.business?.contact_name}</p>
                  <p className="text-sm mt-2" style={{ color: "#6B7280" }}>{dashboard?.business?.contact_email}</p>
                  {dashboard?.business?.contact_phone && <p className="text-sm mt-1" style={{ color: "#6B7280" }}>{dashboard.business.contact_phone}</p>}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}